import { WSButton, WSList } from "@wingspanhq/fe-component-library";
import React, { useState } from "react";
import { useWatch } from "react-hook-form";
import { resolveFieldComponent } from "../constants/fieldTypeComponentMap";
import { IFormNote, IFormSchemaField } from "../types";
import { isFieldDisabled, isHiddenUiField } from "../utils/fieldRules";
import { isServerOwnedValueField } from "../utils/isSharedPiiField";
import { DynamicFormNote } from "./DynamicFormNote";
import { DynamicFieldAutoCalculated } from "./fields/DynamicFieldAutoCalculated";
import { DynamicFieldConfirmGuard } from "./fields/DynamicFieldConfirmGuard";
import { DynamicFieldGroup } from "./fields/DynamicFieldGroup";
import { DynamicFieldHidden } from "./fields/DynamicFieldHidden";

// `ui.collapsedCta`: the field renders as a "+ <cta>" add-button until the
// payee opts in — or immediately expanded when a value already exists (draft
// restore, prefill, or a prior filing's answer). Expansion is one-way local
// state; clearing the input keeps it visible for the session.
const CollapsedFieldGate: React.FC<{
  field: IFormSchemaField;
  name: string;
  children: React.ReactNode;
}> = ({ field, name, children }) => {
  const watched = useWatch({ name, defaultValue: field.prefill?.value });
  const [expanded, setExpanded] = useState(false);
  const hasValue =
    watched != null &&
    watched !== "" &&
    !(typeof watched === "number" && Number.isNaN(watched));
  if (expanded || hasValue) return <>{children}</>;
  return (
    <WSButton
      kind="Secondary"
      size="S"
      icon="plus"
      type="button"
      onClick={() => setExpanded(true)}
    >
      {field.ui?.collapsedCta}
    </WSButton>
  );
};

type Props = {
  field: IFormSchemaField;
  /** RHF path override — Group rows render item fields at `group[i].key`. */
  name?: string;
  /** Controller default for field-array rows (RHF v6 requires it there). */
  defaultValue?: unknown;
  /** Force-disable, e.g. every input inside a disabled Group's rows. */
  disabled?: boolean;
};

export const DynamicFormFieldRenderer: React.FC<Props> = ({
  field,
  name = field.key,
  defaultValue,
  disabled: disabledProp
}) => {
  // Hidden-UI fields (worksheet intermediates) render nothing but must stay
  // RHF-registered so their value reaches the submit payload — checked BEFORE
  // computed, since intermediates are usually computed and would otherwise
  // surface as visible auto-calculated tiles.
  if (isHiddenUiField(field)) {
    return <DynamicFieldHidden field={field} name={name} />;
  }

  // Server-computed fields — and source-less server-owned values (a hook-written
  // worksheet result delivered via prefill.value) — render as the read-only
  // "Auto-calculated" box regardless of their underlying type.
  const isComputed =
    field.operations.computed != null || isServerOwnedValueField(field);

  const disabled =
    disabledProp || field.prefill?.editable === false || isFieldDisabled(field);

  // Plain (non-box) info notes of a COMPUTED field render inside its gray
  // Auto-calculated box, directly under the label row (per the design); boxed
  // notes and warnings keep rendering below the field as usual.
  const isPlainInfoNote = (note: IFormNote) =>
    note.level === "info" && note.variant !== "box";
  const computedInnerNotes: IFormNote[] = isComputed
    ? field.notes.filter(isPlainInfoNote)
    : [];

  let fieldElement: React.ReactNode;
  if (field.type === "Group") {
    // Groups are dispatched here (not via fieldTypeComponentMap) so the row
    // renderer can reuse this component for item fields without an import
    // cycle through the component map's module-init object literal.
    fieldElement = (
      <DynamicFieldGroup field={field} name={name} disabled={disabled} />
    );
  } else if (isComputed) {
    // A server-owned display value with nothing to display renders nothing:
    // genuinely computed fields always carry a value, but hook/stamp-written
    // fields (server-owned, no computed op) are empty until the server writes
    // them — an empty gray "Auto-calculated" box is noise (design review).
    if (
      field.operations.computed == null &&
      (field.prefill?.value == null || field.prefill?.value === "")
    ) {
      return null;
    }
    fieldElement = (
      <DynamicFieldAutoCalculated
        field={field}
        name={name}
        defaultValue={defaultValue}
        innerNotes={computedInnerNotes}
      />
    );
  } else {
    const Component = resolveFieldComponent(field.type);
    if (!Component) return null;
    fieldElement = (
      <Component
        field={field}
        name={name}
        disabled={disabled}
        defaultValue={defaultValue}
        value={field.prefill?.value}
      />
    );
  }

  // Confirm-on-change (`ui.confirm`): a null-rendering guard that opens a
  // confirmation modal when the value changes (to `whenEquals` when set) and
  // reverts the change on cancel.
  const confirmGuard = field.ui?.confirm ? (
    <DynamicFieldConfirmGuard field={field} name={name} />
  ) : null;

  const inlineNotes: IFormNote[] = [...field.warnings, ...field.notes].filter(
    note => !computedInnerNotes.includes(note)
  );

  // The collapsed-CTA affordance applies to plain editable inputs only —
  // computed/server-owned boxes and Groups manage their own presentation.
  if (field.ui?.collapsedCta && !isComputed && field.type !== "Group") {
    fieldElement = (
      <CollapsedFieldGate field={field} name={name}>
        {fieldElement}
      </CollapsedFieldGate>
    );
  }

  if (inlineNotes.length === 0) {
    return (
      <>
        {fieldElement}
        {confirmGuard}
      </>
    );
  }

  return (
    <WSList gap="S">
      {fieldElement}
      {confirmGuard}
      {inlineNotes.map((note, i) => (
        <DynamicFormNote key={`${field.key}-note-${i}`} note={note} size="S" />
      ))}
    </WSList>
  );
};
